/**
 * Types for Video Outline feature
 *
 * SIMPLIFIED VERSION 2:
 * - Uses plain text instructions instead of structured data points and element specs
 * - Content planner receives detailed text instructions and adapts to layouts
 * - No {{dataPointId}} references - use actual values in text
 */

export type OutlineStatus =
  | 'generating'        // AI is generating the outline
  | 'draft'             // Outline ready for user review/modification
  | 'approved'          // User approved, ready for slide generation
  | 'generating_slides' // Slides are being generated in parallel
  | 'complete'          // Legacy: All slides generated (kept for backwards compatibility)
  | 'completed';        // All slides generated, ready for video workspace

// ============================================================================
// Narrative Flow - Simple text-based context about story position
// ============================================================================

export type NarrativePosition = 'opening' | 'setup' | 'build' | 'climax' | 'resolution' | 'closing';

/**
 * Simple narrative context using plain text.
 * Describes where this slide fits in the overall story.
 */
export interface NarrativeFlow {
  /** Position in the narrative arc */
  position?: NarrativePosition;
  /** What this slide builds on from previous slides (plain text) */
  comesFrom?: string;
  /** What this slide sets up for following slides (plain text) */
  leadsTo?: string;
}

// ============================================================================
// Demo blocks (a product demo on one slide)
// ============================================================================

/** Screenshots from a 4K Labs Capture, built by demo-choreographer. */
export interface CaptureDemoBlock {
  captureId: string;
  /** Capture-time span this slide covers, in seconds. */
  window: [number, number];
  treatment: 'full-bleed' | 'component';
}

/** One step a live scene takes in the app, landing on a narration word. */
export interface LiveDemoStep {
  op: string;
  target?: unknown;
  /** Text to type, or a `{{slot}}`. */
  text?: string;
  /** The narration line the step lands in. */
  line?: string;
  /** The word in that line the step lands on. */
  word?: string;
  note?: string;
}

/** The live app in the slide (app demos 02/04): planned by demo-planner, built by live-scene-builder. */
export interface LiveDemoBlock {
  kind: 'live';
  appId: string;
  /** A direct screen's path, query included (the scene's data-start). */
  start: string;
  /** App-map screen ids the scene passes, in order. */
  screens: string[];
  /** The app map's `reach` steps to the first screen, run instantly before the reveal. */
  catchUp?: unknown[];
  steps: LiveDemoStep[];
  camera?: string[];
  callouts?: string[];
  /** Personalization slot keys the scene uses. */
  slots?: string[];
}

export type DemoBlock = CaptureDemoBlock | LiveDemoBlock;

// ============================================================================
// Slide Outline (Simplified v2)
// ============================================================================

export interface SlideOutline {
  id: string;                    // e.g., "slide-001"
  order: number;                 // 1, 2, 3... for reordering
  title: string;                 // Slide title
  description: string;           // Content description/instructions for AI
  keyPoints: string[];           // 2-3 bullet points describing content
  layoutId: string;              // e.g., "layout_hero", "layout_two_col"
  layoutName: string;            // Human-readable layout name
  layoutThumbnail?: string;      // Preview image URL for the layout
  storyboardImage?: { url: string; urlExpiresAt: string }; // A generated picture of how the scene will look
  aiReasoning?: string;          // Why AI chose this layout (for transparency)
  userModified?: boolean;        // Track if user changed the layout

  // SIMPLIFIED ENHANCED FIELDS (v2) - All text-based
  /**
   * Detailed visual content instructions for the content planner.
   * Include specific values, examples, and data using plain text with actual values
   * (e.g., "$100,000 investment" not "{{investment_amount}}").
   * Describe what elements should appear, their content, and visual emphasis.
   */
  visualInstructions?: string;

  /**
   * What the viewer should understand or feel after seeing this slide.
   */
  audienceTakeaway?: string;

  /**
   * Context about where this slide fits in the story.
   * Uses simple text descriptions for what it builds on and sets up.
   */
  narrativeFlow?: NarrativeFlow;

  /**
   * Optional interaction intent for interactive slides.
   * When present, signals that this slide requires user interaction
   * (card selection, form input, continue gate, etc.)
   */
  interaction?: {
    type: 'card_selection' | 'numeric_input' | 'text_input' | 'ranking' | 'continue_gate';
    /** Data keys this slide writes to window.__ivySlideData */
    producesData?: string[];
    /** Data keys this slide reads from window.__ivySlideData */
    consumesData?: string[];
  };

  // Generation tracking fields (added during slide generation)
  slideConversationId?: string;  // Child conversation ID for this slide
  generationStatus?: 'pending' | 'generating' | 'completed' | 'failed';
  generationError?: string;      // Error message if failed
  generatingThinking?: string;   // Live thinking text during generation
  generatedAt?: string;          // ISO timestamp when slide was generated
  generatedThumbnail?: string;   // Thumbnail URL after generation
  generationStartedAt?: string;  // ISO timestamp when generation started (for stuck detection)
  isStuck?: boolean;             // Backend flag indicating job may be stuck

  // URL personalization: slot keys this slide wires.
  usesSlots?: string[];

  /** A product demo on this slide: screenshots from a capture, or the live app (/create-video-demo). */
  demo?: DemoBlock;

  // Audio-first fields (continuous master audio mode)
  /** The text segment from the master audio transcript for this slide */
  scriptSegment?: string;
  /** Absolute time range in master audio for this slide's content */
  audioRange?: { startTime: number; endTime: number };
  /** Seam (the boundary AFTER this slide) metadata for the guided split-correction UI.
   *  Written by split_master_audio so the storyboard can render snap targets + triage
   *  without an extra round-trip. Absent on the last slide. */
  seam?: SeamMeta;
  /** User-pinned cut for this slide's END boundary — survives re-splits (read back by
   *  split_master_audio). source 'user' = placed in the seam editor. */
  boundaryLock?: { cutTime: number; source: 'user' };

  // Soft delete fields (for recoverable slide deletion)
  isDeleted?: boolean;           // True if slide has been soft deleted
  deletedAt?: string;            // ISO timestamp when slide was deleted

  // Storyboard metadata (kubicle disciplined-compose director: intent routing + archetype choice)
  storyboard?: StoryboardMeta;
}

/** Seam (boundary AFTER a slide) metadata for the guided split-correction UI.
 *  Written by split_master_audio onto each slide so the storyboard can show snap
 *  targets + a needs-attention flag without an extra round-trip. */
export interface SeamMeta {
  seamIndex?: number;
  beforeSlideId?: string;
  afterSlideId: string;
  /** Current cut (= this slide's end / next slide's start), absolute seconds. */
  cutTime: number;
  /** True when the cut was user-pinned (boundaryLock). */
  locked: boolean;
  /** True when the auto cut is low-confidence / not on a clean pause. */
  needsAttention: boolean;
  confidence: { cutSource: string; matchScore: number };
  /** Focused ±window around the cut the editor renders. */
  window: { start: number; end: number };
  /** Pauses inside the window — the magnetic snap targets. */
  silenceGaps: Array<{ start: number; end: number; mid: number; durationMs: number }>;
  leadingWords: Array<{ word: string; startTime: number; endTime: number }>;
  trailingWords: Array<{ word: string; startTime: number; endTime: number }>;
  /** Normalized (0–1) amplitude peaks across the window, left→right — the waveform. */
  peaks?: number[];
  /** Sample-accurate WAV of the window (same PCM as the waveform), played window-relative
   *  so preview lines up with the waveform. */
  windowAudioUrl?: string;
}

/** Per-scene director decisions surfaced in the Storyboard tab (disciplined-compose flow). */
export interface StoryboardMeta {
  archetypeKey?: string;         // golden archetype seeded for this scene, e.g. "AIWorkflowDiagramV1"
  narrationShape?: string;       // classified intent, e.g. "fan-out-flow" | "contrast" | "single-concept"
  selectionRationale?: string;   // why this archetype = this sentence (the one-line director rationale)
  tier?: 'A' | 'B' | 'C';        // A = golden archetype, B = composed, C = novel/generative
  surface?: 'light' | 'dark';    // Law A surface
  chapterLabel?: string;         // chapter pill this scene belongs to (groups scenes)
  sceneIndex?: string;           // "N/M" position within the chapter
  previewUrl?: string;           // resolved archetype preview image (Phase 2)
}

// ============================================================================
// Video Outline (Simplified v2)
// ============================================================================

export interface VideoOutline {
  version: number;               // Schema version (2 for simplified enhanced)
  status: OutlineStatus;
  title: string;                 // Video title
  templateId: string;            // Selected template ID
  templateName: string;          // Human-readable template name
  generatedAt: string;           // ISO timestamp when outline was generated
  approvedAt?: string;           // ISO timestamp when user approved
  slides: SlideOutline[];
  branding?: BrandingConfig;     // Company branding configuration

  // Resolution override (e.g. "1080x1080" for 1:1). Defaults to "1920x1080" if absent.
  resolution?: string;

  /** Signed URL of the master audio, for previewing the window around a seam in the
   *  split-correction UI. Written by split_master_audio (best-effort; may expire). */
  seamAudioUrl?: string;

  // Generative mode fields (blank-canvas)
  generationMode?: 'generative';
  designSystemId?: string;       // 'cinematic' | 'editorial' — selectable design system
  designSystem?: {               // Video-level design tokens
    colors: Record<string, string>;
    typography: { headlineFont: string; bodyFont: string };
  };

  // Script-first / Audio-first fields
  creationMode?: 'standard' | 'script_first' | 'audio_first' | 'footage';
  /** The look: its ref, its display name, and who chose it. */
  lookRef?: string;
  lookLabel?: string;
  lookSource?: string;
  fullScript?: string;
  masterAudio?: {
    gcsPath: string;
    originalName: string;
    duration: number;
    transcript: string;
    resourceId: string;
    wordTimings?: Array<{ word: string; startTime: number; endTime: number; confidence: number }>;
  };

  // URL personalization slots this video accepts (provider-neutral; additive).
  personalization?: {
    slots: Array<{
      key: string;
      type?: 'text' | 'url' | 'number' | 'percent';
      description?: string;
      example?: string;
      allowedDomains?: string[];
    }>;
    allowedRedirectDomains?: string[];
    requireSignature?: boolean;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Checks if a slide has any enhanced content fields.
 */
export function hasEnhancedContent(slide: SlideOutline): boolean {
  return !!(slide.visualInstructions || slide.audienceTakeaway || slide.narrativeFlow);
}

/**
 * Checks if an outline has any slides with enhanced content.
 */
export function outlineHasEnhancedContent(outline: VideoOutline): boolean {
  return outline.slides.some(hasEnhancedContent);
}

// ============================================================================
// Company Branding Configuration
// ============================================================================

export interface BrandingConfig {
  colors: {
    primary: string;      // Hex code - main brand color (required)
    secondary: string;    // Hex code - accent/supporting color (required)
    accent?: string;      // Optional third color for highlights
    background?: string;  // Slide background override
    textPrimary?: string; // Primary text color override
  };
  fonts?: {
    headline: string;     // Font family for headlines (e.g., "Inter")
    body?: string;        // Font family for body text
  };
  logo?: {
    url: string;          // GCS signed URL
    gcsPath: string;      // Persistent GCS path
    position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
    maxHeight?: number;   // Default: 60px
  };
  appliedAt?: string;     // ISO timestamp when branding was applied
}

// ============================================================================
// Layout Info from Template
// ============================================================================

export interface TemplateLayout {
  id: string;                    // Template UUID (database ID)
  layoutId: string;              // Layout identifier e.g., "layout_01", "layout_hero"
  name: string;                  // Human-readable name
  thumbnail?: string;            // Preview image URL
  description?: string;          // Brief description of the layout
  category?: string;             // e.g., "opening", "content", "closing"
}

// ============================================================================
// API Response Types
// ============================================================================

export interface GenerateOutlineResponse {
  status: 'generating';
  jobId: string;
}

export type GetOutlineResponse = VideoOutline | null;

export interface UpdateOutlineResponse {
  success: boolean;
  version: number;
}

export interface ApproveOutlineResponse {
  success: boolean;
  status: 'generating_slides';
}
